// ---------------------------------------------------------------------------
// Layer 0.8: Visionary Foundation Tables (Tenant DB)
// ---------------------------------------------------------------------------
// Empty foundation tables for: AI agent personas, legal-grade accountability,
// cross-tenant vertical intelligence, prediction feedback loops.
//
// These tables are FREE when empty. The constraint retrofits are NOT free
// after production data exists. Add now, fill later.
//
// RETENTION NOTE: ai_accountability_log and prediction_outcomes grow unbounded.
// A retention policy (e.g., 2 years for accountability, 1 year for predictions)
// MUST be implemented in Layer 2 before production scale. Track in master plan.
// ---------------------------------------------------------------------------

import { pgTable, uuid, text, boolean, integer, jsonb, timestamp, index, check } from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";

const tz = { withTimezone: true } as const;

// ---------------------------------------------------------------------------
// A.1: AI Agent Persona Configuration
// ---------------------------------------------------------------------------
// Per-tenant AI agent personas. Controls name, personality, confidence
// threshold, autonomy level, allowed/blocked MCP actions, escalation rules.
// Default: inactive, suggest-only, max 50 actions/day.

export const tenantAiAgents = pgTable("tenant_ai_agents", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  name: text("name").notNull(),
  personality: text("personality"),
  avatarUrl: text("avatar_url"), // v2 addition: agent avatar
  voiceProfile: text("voice_profile"), // v2 addition: "professional" | "casual" | "friendly"
  confidenceThreshold: integer("confidence_threshold").notNull().default(70),
  allowedActions: text("allowed_actions").array(),
  disallowedActions: text("disallowed_actions").array(),
  escalationRules: jsonb("escalation_rules"),
  autonomyLevel: text("autonomy_level").notNull().default("suggest_only"),
  maxActionsPerDay: integer("max_actions_per_day").notNull().default(50),
  isActive: boolean("is_active").notNull().default(false),
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
}, (table) => [
  index("idx_ai_agents_tenant").on(table.tenantId),
  check(
    "chk_ai_agent_autonomy_level",
    sql`${table.autonomyLevel} IN ('suggest_only', 'suggest_and_draft', 'execute_with_approval', 'fully_autonomous')`,
  ),
]);

// ---------------------------------------------------------------------------
// A.1b: AI Agent Tasks
// ---------------------------------------------------------------------------
// Tenant-scoped persisted task state for approval-gated agent runs.
// Stores the current task snapshot plus execution history so the route layer
// can survive worker restarts and hydrate the UI across sessions.

export const aiAgentTasks = pgTable("ai_agent_tasks", {
  id: text("id").primaryKey(),
  tenantId: uuid("tenant_id").notNull(),
  ownerUserId: text("owner_user_id").notNull(),
  agentType: text("agent_type").notNull(),
  goal: text("goal").notNull(),
  context: jsonb("context").$type<Record<string, unknown>>().default({}).notNull(),
  boundaries: jsonb("boundaries").$type<Record<string, unknown>>().notNull(),
  state: text("state").notNull().default("idle"),
  proposedAction: jsonb("proposed_action").$type<Record<string, unknown> | null>(),
  result: jsonb("result").$type<Record<string, unknown> | null>(),
  executionHistory: jsonb("execution_history").$type<Record<string, unknown>[]>().default([]).notNull(),
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
}, (table) => [
  index("idx_ai_agent_tasks_owner").on(table.tenantId, table.ownerUserId, table.updatedAt),
  index("idx_ai_agent_tasks_state").on(table.tenantId, table.state, table.updatedAt),
  index("idx_ai_agent_tasks_agent_type").on(table.tenantId, table.agentType, table.updatedAt),
  check(
    "chk_ai_agent_task_state",
    sql`${table.state} IN ('idle', 'thinking', 'action_proposed', 'approved', 'executing', 'complete', 'failed', 'rejected')`,
  ),
]);

// ---------------------------------------------------------------------------
// A.2: AI Accountability Log
// ---------------------------------------------------------------------------
// APPEND-ONLY legal-grade audit trail for every AI action.
// EU AI Act Art. 13 (transparency) + Art. 14 (human oversight).
// No updatedAt, no soft delete — permanent immutable record.
//
// GDPR erasure: DELETE FROM ai_accountability_log WHERE tenant_id = $1
// on tenant deletion cascade.

export const aiAccountabilityLog = pgTable("ai_accountability_log", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  agentId: uuid("agent_id"),
  actionType: text("action_type").notNull(),
  triggerSource: text("trigger_source").notNull(),
  prompt: text("prompt"),
  reasoning: jsonb("reasoning"),
  confidenceScore: integer("confidence_score"),
  approvalStatus: text("approval_status").notNull().default("pending"),
  approvedBy: uuid("approved_by"),
  inputHash: text("input_hash"),
  outputHash: text("output_hash"),
  resultSummary: text("result_summary"),
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  // APPEND-ONLY: no updatedAt, no soft delete
}, (table) => [
  index("idx_ai_accountability_tenant").on(table.tenantId, table.createdAt),
  index("idx_ai_accountability_agent").on(table.agentId),
  index("idx_ai_accountability_status").on(table.approvalStatus),
  check(
    "chk_ai_accountability_approval_status",
    sql`${table.approvalStatus} IN ('auto_approved', 'human_approved', 'pending', 'rejected', 'executed_autonomous')`,
  ),
  check(
    "chk_ai_accountability_trigger_source",
    sql`${table.triggerSource} IN ('user_command', 'workflow', 'autonomous', 'scheduled')`,
  ),
]);

// ---------------------------------------------------------------------------
// A.2b: Agent Escalation Log
// ---------------------------------------------------------------------------
// When agents escalate to humans: why, to whom, resolution, response time.
// Complements ai_accountability_log (which tracks all actions).
// This specifically tracks the human handoff moments.

export const agentEscalationLog = pgTable("agent_escalation_log", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  agentId: uuid("agent_id").notNull(),
  reason: text("reason").notNull(),
    // "low_confidence" | "restricted_action" | "customer_request" | "complex_query"
  confidenceScore: integer("confidence_score"),
  escalatedTo: uuid("escalated_to"),
  conversationId: uuid("conversation_id"),
  contactId: uuid("contact_id"),
  context: jsonb("context"),
  resolution: text("resolution"),
    // "resolved_by_human" | "returned_to_agent" | "closed" | null (pending)
  resolvedBy: uuid("resolved_by"),
  resolvedAt: timestamp("resolved_at", tz),
  responseTimeMs: integer("response_time_ms"),
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
}, (table) => [
  index("idx_agent_escalation_tenant").on(table.tenantId, table.createdAt),
  index("idx_agent_escalation_agent").on(table.tenantId, table.agentId),
  index("idx_agent_escalation_user").on(table.tenantId, table.escalatedTo),
]);

// ---------------------------------------------------------------------------
// A.3: Vertical Insights (Platform-Level)
// ---------------------------------------------------------------------------
// Cross-tenant industry intelligence accumulation. NO tenantId — this is
// the "collective intelligence moat." Stores learned patterns by industry.
// Application code must NEVER pass tenant-identifiable data here.

export const verticalInsights = pgTable("vertical_insights", {
  id: uuid("id").primaryKey().defaultRandom(),
  industry: text("industry").notNull(),
  insightType: text("insight_type").notNull(),
  parameters: jsonb("parameters").notNull(),
  value: jsonb("value").notNull(),
  sampleSize: integer("sample_size").notNull().default(0),
  confidenceScore: integer("confidence_score"),
  lastComputedAt: timestamp("last_computed_at", tz).notNull().defaultNow(),
  validUntil: timestamp("valid_until", tz),
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
}, (table) => [
  index("idx_vertical_insights_industry").on(table.industry, table.insightType),
]);

// ---------------------------------------------------------------------------
// A.3: Prediction Outcomes (Continuous Learning Readiness)
// ---------------------------------------------------------------------------
// Records every prediction alongside what actually happened.
// The delta (actual - predicted) is the feedback loop for future ML.

export const predictionOutcomes = pgTable("prediction_outcomes", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  predictionType: text("prediction_type").notNull(),
  predictionId: text("prediction_id").notNull(),
  predictedValue: integer("predicted_value").notNull(),
  actualValue: integer("actual_value"),
  delta: integer("delta"),
  features: jsonb("features"),
  outcomeObservedAt: timestamp("outcome_observed_at", tz),
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
}, (table) => [
  index("idx_prediction_outcomes_tenant").on(table.tenantId, table.predictionType),
  index("idx_prediction_outcomes_type").on(table.predictionType, table.createdAt),
]);
